import { formatMinor, type MileageLog, type VehicleYear } from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Skeleton,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { bookkeepingReferenceQuery } from '../../shared/api/bookkeeping-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  addMileage,
  archiveVehicle,
  mileageQuery,
  postVehicleAdjustment,
  removeMileage,
  saveVehicle,
  vehicleKeys,
  vehiclesQuery,
} from '../../shared/api/vehicle-queries.js';

/**
 * Vehicles: each truck, whose it is, and how much of it is business.
 *
 * Fuel, repair and insurance lines carry their vehicle (tagged automatically
 * when the spender drives one, by hand otherwise). The year's costs show per
 * vehicle with the share that was not business and the tax claimed on it;
 * one adjusting entry at year-end moves both where they belong. A mileage
 * log, when kept, says what the share should be.
 */

const today = () => new Date().toISOString().slice(0, 10);

export function VehiclesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const membership = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  );
  const canWrite = membership?.permissions.includes('transaction:update') ?? false;
  const canLog = membership?.permissions.includes('storage_object:create') ?? false;
  const queryClient = useQueryClient();
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const vehicles = useQuery(vehiclesQuery(organizationId, fiscalYear));
  const [editing, setEditing] = useState<VehicleYear | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: vehicleKeys.all(organizationId) });
    void queryClient.invalidateQueries({ queryKey: ['reports', organizationId] });
    void queryClient.invalidateQueries({ queryKey: ['bookkeeping', organizationId] });
  };

  const rows = vehicles.data ?? [];
  const personal = rows.reduce(
    (sum, row) => sum + BigInt(row.personalShareMinor) + BigInt(row.itcRepayMinor),
    0n,
  );
  const post = useMutation({
    mutationFn: () => postVehicleAdjustment(organizationId, fiscalYear),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The adjustment was not posted.'),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Business"
        title="Vehicles"
        description={
          vehicles.data === undefined
            ? 'Reading…'
            : rows.length === 0
              ? 'No vehicles yet. Add each truck with its business-use share; fuel, repair and insurance receipts then carry it.'
              : `${String(rows.filter((row) => !row.archived).length)} vehicle${rows.filter((row) => !row.archived).length === 1 ? '' : 's'}. ${personal === 0n ? 'Every one is 100% business this year.' : `${formatMinor(personal.toString())} of ${String(fiscalYear)}'s vehicle costs and tax credits are personal use.`}`
        }
        action={
          canWrite && editing === null ? (
            <Button onClick={() => setEditing('new')}>Add a vehicle</Button>
          ) : undefined
        }
      />

      {error === null ? null : <Alert tone="danger">{error}</Alert>}

      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-content-muted">Fiscal year</span>
          <Input
            inputMode="numeric"
            value={String(fiscalYear)}
            className="w-28"
            onChange={(event) => {
              const next = Number(event.target.value);
              if (Number.isInteger(next) && next >= 2000 && next <= 2100) setFiscalYear(next);
            }}
          />
        </label>
        {!canWrite || personal === 0n ? null : (
          <Button variant="secondary" onClick={() => post.mutate()} isLoading={post.isPending}>
            Post the {String(fiscalYear)} personal-use adjustment
          </Button>
        )}
      </div>

      {editing === null ? null : (
        <VehicleEditor
          organizationId={organizationId}
          initial={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {vehicles.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        rows.map((row) => (
          <VehicleCard
            key={row.vehicleId}
            organizationId={organizationId}
            fiscalYear={fiscalYear}
            vehicle={row}
            canWrite={canWrite}
            canLog={canLog}
            onEdit={() => setEditing(row)}
            onChanged={refresh}
            onError={setError}
          />
        ))
      )}
    </div>
  );
}

function VehicleCard({
  organizationId,
  fiscalYear,
  vehicle,
  canWrite,
  canLog,
  onEdit,
  onChanged,
  onError,
}: {
  organizationId: string;
  fiscalYear: number;
  vehicle: VehicleYear;
  canWrite: boolean;
  canLog: boolean;
  onEdit: () => void;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const [logOpen, setLogOpen] = useState(false);
  const archive = useMutation({
    mutationFn: () => archiveVehicle(organizationId, vehicle.vehicleId),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The vehicle was not archived.'),
  });
  const costs =
    BigInt(vehicle.fuelMinor) + BigInt(vehicle.repairsMinor) + BigInt(vehicle.insuranceMinor);
  const share = Number(vehicle.businessUsePercent);
  const logShare = vehicle.logSharePercent === null ? null : Number(vehicle.logSharePercent);
  return (
    <Card>
      <CardHeader
        title={`${vehicle.name}${vehicle.plate === null ? '' : ` · ${vehicle.plate}`}${vehicle.archived ? ' (archived)' : ''}`}
        description={`${String(share)}% business${vehicle.technicianName === null ? '' : `, driven by ${vehicle.technicianName}`}.${logShare === null ? ' No mileage log this year.' : ` The log says ${String(logShare)}% (${vehicle.kmBusiness} of ${vehicle.kmTotal} km).`}${logShare !== null && Math.abs(logShare - share) >= 5 ? ' The log and the share disagree; set the share from the log.' : ''}`}
        actions={
          canWrite && !vehicle.archived ? (
            <span className="inline-flex gap-2">
              <Button size="sm" variant="secondary" onClick={onEdit}>
                Edit
              </Button>
              <button
                type="button"
                className="text-xs text-content-muted hover:text-danger"
                onClick={() => archive.mutate()}
              >
                Archive
              </button>
            </span>
          ) : undefined
        }
      />
      <CardBody className="flex flex-col gap-3">
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-6">
          <Item label="Fuel" value={formatMinor(vehicle.fuelMinor)} />
          <Item label="Repairs" value={formatMinor(vehicle.repairsMinor)} />
          <Item label="Insurance" value={formatMinor(vehicle.insuranceMinor)} />
          <Item label="Tax claimed back" value={formatMinor(vehicle.itcMinor)} />
          <Item
            label="Personal share of costs"
            value={formatMinor(vehicle.personalShareMinor)}
            tone={vehicle.personalShareMinor === '0' ? undefined : 'warn'}
          />
          <Item
            label="Tax to give back"
            value={formatMinor(vehicle.itcRepayMinor)}
            tone={vehicle.itcRepayMinor === '0' ? undefined : 'warn'}
          />
        </dl>
        {costs === 0n ? (
          <p className="text-xs text-content-muted">
            No signed fuel, repair or insurance lines carry this vehicle in {String(fiscalYear)}{' '}
            yet. Tag them on the record, or set the driver so they tag themselves.
          </p>
        ) : null}
        <div>
          <button
            type="button"
            className="text-sm text-brand-700 hover:underline"
            onClick={() => setLogOpen((before) => !before)}
          >
            {logOpen ? 'Hide the mileage log' : 'Mileage log'}
          </button>
        </div>
        {logOpen ? (
          <Mileage
            organizationId={organizationId}
            fiscalYear={fiscalYear}
            vehicleId={vehicle.vehicleId}
            canLog={canLog}
            canWrite={canWrite}
            onChanged={onChanged}
            onError={onError}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

function Item({ label, value, tone }: { label: string; value: string; tone?: 'warn' | undefined }) {
  return (
    <div>
      <dt className="text-xs text-content-muted">{label}</dt>
      <dd className={['tabular font-medium', tone === 'warn' ? 'text-warning' : ''].join(' ')}>
        {value}
      </dd>
    </div>
  );
}

function Mileage({
  organizationId,
  fiscalYear,
  vehicleId,
  canLog,
  canWrite,
  onChanged,
  onError,
}: {
  organizationId: string;
  fiscalYear: number;
  vehicleId: string;
  canLog: boolean;
  canWrite: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const logs = useQuery(mileageQuery(organizationId, fiscalYear));
  const [loggedOn, setLoggedOn] = useState(today);
  const [km, setKm] = useState('');
  const [business, setBusiness] = useState(true);
  const [purpose, setPurpose] = useState('');
  const add = useMutation({
    mutationFn: () =>
      addMileage(organizationId, {
        vehicleId,
        loggedOn,
        kilometres: Number(km),
        business,
        purpose: purpose.trim() === '' ? null : purpose.trim(),
      }),
    onSuccess: () => {
      setKm('');
      setPurpose('');
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The trip was not logged.'),
  });
  const remove = useMutation({
    mutationFn: (logId: string) => removeMileage(organizationId, logId),
    onSuccess: () => {
      onError(null);
      onChanged();
    },
    onError: (caught) =>
      onError(caught instanceof Error ? caught.message : 'The trip was not removed.'),
  });
  const mine: MileageLog[] = (logs.data ?? []).filter((log) => log.vehicleId === vehicleId);
  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-border p-3">
      {canLog ? (
        <div className="grid gap-3 sm:grid-cols-[9rem_7rem_8rem_1fr_auto] sm:items-end">
          <Field label="Date">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="date"
                value={loggedOn}
                onChange={(event) => setLoggedOn(event.target.value)}
              />
            )}
          </Field>
          <Field label="Kilometres">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                inputMode="decimal"
                value={km}
                onChange={(event) => setKm(event.target.value)}
              />
            )}
          </Field>
          <Field label="Trip">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={business ? 'business' : 'personal'}
                onChange={(event) => setBusiness(event.target.value === 'business')}
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="business">Business</option>
                <option value="personal">Personal</option>
              </select>
            )}
          </Field>
          <Field label="Where to">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={purpose}
                placeholder="Doucet job, Caraquet"
                onChange={(event) => setPurpose(event.target.value)}
              />
            )}
          </Field>
          <Button
            size="sm"
            onClick={() => add.mutate()}
            isLoading={add.isPending}
            disabled={!(Number(km) > 0) || loggedOn === ''}
          >
            Log it
          </Button>
        </div>
      ) : null}
      {mine.length === 0 ? (
        <p className="text-xs text-content-muted">No trips logged for {String(fiscalYear)}.</p>
      ) : (
        <table className="w-full text-sm tabular">
          <tbody>
            {mine.map((log) => (
              <tr key={log.id} className="border-t border-border">
                <td className="py-1">{log.loggedOn}</td>
                <td className="py-1 text-right">{log.kilometres} km</td>
                <td className="py-1 text-content-muted">
                  {log.business ? 'business' : 'personal'}
                </td>
                <td className="py-1 text-content-muted">{log.purpose ?? ''}</td>
                <td className="py-1 text-right">
                  {canWrite ? (
                    <button
                      type="button"
                      className="text-xs text-content-muted hover:text-danger"
                      onClick={() => remove.mutate(log.id)}
                    >
                      Remove
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function VehicleEditor({
  organizationId,
  initial,
  onDone,
  onCancel,
}: {
  organizationId: string;
  initial: VehicleYear | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const [name, setName] = useState(initial?.name ?? '');
  const [plate, setPlate] = useState(initial?.plate ?? '');
  const [technicianId, setTechnicianId] = useState(initial?.technicianId ?? '');
  const [share, setShare] = useState(initial?.businessUsePercent ?? '100');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      saveVehicle(organizationId, {
        vehicleId: initial?.vehicleId ?? null,
        name: name.trim(),
        plate: plate.trim() === '' ? null : plate.trim(),
        technicianId: technicianId === '' ? null : technicianId,
        businessUsePercent: Number(share),
      }),
    onSuccess: onDone,
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The vehicle was not saved.'),
  });
  const valid = name.trim() !== '' && Number(share) >= 0 && Number(share) <= 100;
  return (
    <Card>
      <CardHeader
        title={initial === null ? 'Add a vehicle' : `Edit ${initial.name}`}
        description="The business-use share is the owner's honest estimate until a mileage log says otherwise. The CRA accepts a log kept for a representative three months."
      />
      <CardBody className="flex flex-col gap-4">
        {error === null ? null : <Alert tone="danger">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-4">
          <Field label="Name" required>
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={name}
                placeholder="2022 Transit"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
          <Field label="Plate">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={plate}
                onChange={(event) => setPlate(event.target.value)}
              />
            )}
          </Field>
          <Field label="Driven by" hint="Their fuel and repair receipts tag this vehicle.">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={technicianId}
                onChange={(event) => setTechnicianId(event.target.value)}
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="">Nobody in particular</option>
                {(reference.data?.employees ?? []).map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.fullName}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Business use %" required>
            {({ inputProps }) => (
              <Input
                {...inputProps}
                inputMode="decimal"
                value={share}
                onChange={(event) => setShare(event.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save.mutate()} isLoading={save.isPending} disabled={!valid}>
            Save
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
